// src/components/Inventory.js - Updated with centered search functionality
import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const Inventory = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'lowstock', 'search'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Update inventory state
  const [updateForm, setUpdateForm] = useState({
    productId: '',
    quantity: 0
  });
  const [updating, setUpdating] = useState(false);

  const { user } = useAuth();
  
  // Check if user has admin role for inventory management
  const isAdmin = user && user.authorities && 
    user.authorities.some(auth => auth.authority === 'ROLE_ADMIN');

  useEffect(() => {
    loadInventoryData();
  }, []);

  const loadInventoryData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Your inventory API returns products directly, not separate inventory items
      const inventoryData = await apiService.getAllInventory();
      console.log('Inventory data received:', inventoryData);
      
      // Ensure it's an array
      const inventoryArray = Array.isArray(inventoryData) ? inventoryData : [];
      setInventory(inventoryArray);
      
    } catch (err) {
      console.error('Failed to load inventory:', err);
      setError(`Failed to load inventory data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateInventoryQuantity = async (e) => {
    e.preventDefault();
    if (!updateForm.productId || updateForm.quantity < 0) {
      setError('Please select a product and enter a valid quantity');
      return;
    }

    try {
      setUpdating(true);
      setError(null);

      await apiService.updateInventory(updateForm.productId, updateForm.quantity);
      
      // Reload inventory data to reflect changes
      await loadInventoryData();
      
      setUpdateForm({ productId: '', quantity: 0 });
      alert('Inventory updated successfully!');
    } catch (err) {
      setError(`Failed to update inventory: ${err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const viewItemDetails = async (productId) => {
    try {
      const itemDetails = await apiService.getInventoryById(productId);
      setSelectedItem(itemDetails);
    } catch (err) {
      // If specific item API fails, use the item from current inventory
      const item = inventory.find(item => item.id === productId);
      if (item) {
        setSelectedItem(item);
      } else {
        setError(`Failed to load item details: ${err.message}`);
      }
    }
  };

  const getStockStatus = (quantity) => {
    if (quantity === 0) return { status: 'OUT_OF_STOCK', color: '#dc3545', text: 'Out of Stock' };
    if (quantity < 10) return { status: 'LOW_STOCK', color: '#ffc107', text: 'Low Stock' };
    if (quantity < 50) return { status: 'MEDIUM_STOCK', color: '#17a2b8', text: 'Medium Stock' };
    return { status: 'HIGH_STOCK', color: '#28a745', text: 'In Stock' };
  };

  // Enhanced filtering function with search
  const getFilteredInventory = () => {
    if (!searchTerm) return inventory;
    
    const searchLower = searchTerm.toLowerCase();
    return inventory.filter(item => {
      return (
        item.id.toString().includes(searchTerm) ||
        item.name?.toLowerCase().includes(searchLower) ||
        item.sku?.toLowerCase().includes(searchLower) ||
        item.category?.toLowerCase().includes(searchLower) ||
        item.brand?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower)
      );
    });
  };

  const filteredInventory = getFilteredInventory();
  const lowStockItems = inventory.filter(item => item.quantity < 10);

  const calculateTotalValue = () => {
    return inventory.reduce((total, item) => {
      return total + (item.quantity * item.price);
    }, 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return <div className="loading">Loading inventory...</div>;
  }

  return (
    <div className="inventory-container">
      <div className="inventory-header">
        <h1></h1>
        <div className="tab-buttons">
          <button 
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`tab-btn ${activeTab === 'lowstock' ? 'active' : ''}`}
            onClick={() => setActiveTab('lowstock')}
          >
            Low Stock ({lowStockItems.length})
          </button>
          <button 
            className={`tab-btn ${activeTab === 'search' ? 'active' : ''}`}
            onClick={() => setActiveTab('search')}
          >
            Search & Update
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-btn">×</button>
        </div>
      )}

      {/* Inventory Summary */}
      <div className="inventory-summary">
        <div className="summary-cards">
          <div className="summary-card">
            <h3>Total Items</h3>
            <div className="summary-number">{inventory.length}</div>
          </div>
          <div className="summary-card">
            <h3>Total Stock</h3>
            <div className="summary-number">
              {inventory.reduce((total, item) => total + item.quantity, 0)}
            </div>
          </div>
          <div className="summary-card">
            <h3>Low Stock Items</h3>
            <div className="summary-number warning">{lowStockItems.length}</div>
          </div>
          <div className="summary-card">
            <h3>Total Value</h3>
            <div className="summary-number">${calculateTotalValue().toFixed(2)}</div>
          </div>
        </div>
      </div>

      {activeTab === 'overview' && (
        <div className="inventory-overview">
          {/* Updated header structure for proper centering */}
          <div className="orders-header-section">
            <div className="orders-title-section">
              <h3>All Inventory Items ({filteredInventory.length})</h3>
            </div>
            
            <div className="search-container-centered">
              <input
                type="text"
                placeholder="Search by name, SKU, category, brand..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input-centered"
              />
            </div>
            
            <div className="pagination-info-section">
              <button onClick={loadInventoryData} className="refresh-btn">Refresh</button>
            </div>
          </div>

          {filteredInventory.length === 0 ? (
            <div className="no-inventory">
              <p>No inventory items found matching your search.</p>
            </div>
          ) : (
            <div className="orders-table-modern">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Product Name</th>
                    <th>SKU</th>
                    <th>Category</th>
                    <th>Current Stock</th>
                    <th>Status</th>
                    <th>Unit Price</th>
                    <th>Total Value</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.map(item => {
                    const stockStatus = getStockStatus(item.quantity);
                    return (
                      <tr key={item.id}>
                        <td className="order-id-cell">{item.id}</td>
                        <td className="product-name-cell">{item.name}</td>
                        <td className="customer-id-cell">{item.sku}</td>
                        <td className="category-cell">{item.category || 'N/A'}</td>
                        <td className="qty-cell-modern">
                          <span className={`quantity-display ${item.quantity < 10 ? 'low-stock' : ''}`}>
                            {item.quantity}
                          </span>
                        </td>
                        <td>
                          <span 
                            className="status-badge-modern"
                            style={{ 
                              color: stockStatus.color, 
                              backgroundColor: "white", 
                              borderColor: stockStatus.color, 
                              border: "2px solid"
                            }}
                          >
                            {stockStatus.text}
                          </span>
                        </td>
                        <td className="amount-cell-modern">${item.price?.toFixed(2)}</td>
                        <td className="amount-cell-modern">${(item.quantity * item.price).toFixed(2)}</td>
                        <td>
                          <button 
                            className="action-btn-modern" 
                            onClick={() => viewItemDetails(item.id)}
                            title="View Item Details"
                          >
                            V
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'lowstock' && (
        <div className="low-stock-section">
          {/* Updated header structure for low stock section */}
          <div className="orders-header-section">
            <div className="orders-title-section">
              <h3>Low Stock Items ({lowStockItems.length})</h3>
              <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#6c757d' }}>
                Items with quantity less than 10 units
              </p>
            </div>
            
            <div className="search-container-centered">
              <input
                type="text"
                placeholder="Search low stock items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input-centered"
              />
            </div>
            
            <div className="pagination-info-section">
              <button onClick={loadInventoryData} className="refresh-btn">Refresh</button>
            </div>
          </div>

          {lowStockItems.filter(item => {
            if (!searchTerm) return true;
            const searchLower = searchTerm.toLowerCase();
            return (
              item.id.toString().includes(searchTerm) ||
              item.name?.toLowerCase().includes(searchLower) ||
              item.sku?.toLowerCase().includes(searchLower) ||
              item.category?.toLowerCase().includes(searchLower)
            );
          }).length === 0 ? (
            <div className="no-low-stock">
              <p>{searchTerm ? 'No low stock items match your search.' : 'Great! No items are currently low in stock.'}</p>
            </div>
          ) : (
            <div className="orders-table-modern">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Product Name</th>
                    <th>SKU</th>
                    <th>Category</th>
                    <th>Current Stock</th>
                    <th>Status</th>
                    <th>Unit Price</th>
                    <th>Total Value</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStockItems
                    .filter(item => {
                      if (!searchTerm) return true;
                      const searchLower = searchTerm.toLowerCase();
                      return (
                        item.id.toString().includes(searchTerm) ||
                        item.name?.toLowerCase().includes(searchLower) ||
                        item.sku?.toLowerCase().includes(searchLower) ||
                        item.category?.toLowerCase().includes(searchLower)
                      );
                    })
                    .map(item => {
                      const stockStatus = getStockStatus(item.quantity);
                      return (
                        <tr key={item.id} className="low-stock-row">
                          <td className="order-id-cell">{item.id}</td>
                          <td className="product-name-cell">{item.name}</td>
                          <td className="customer-id-cell">{item.sku}</td>
                          <td className="category-cell">{item.category || 'N/A'}</td>
                          <td className="qty-cell-modern">
                            <span className="quantity-display urgent">
                              {item.quantity}
                            </span>
                          </td>
                          <td>
                            <span 
                              className="status-badge-modern urgent"
                              style={{ 
                                color: stockStatus.color, 
                                backgroundColor: "white", 
                                borderColor: stockStatus.color, 
                                border: "2px solid"
                              }}
                            >
                              {stockStatus.text}
                            </span>
                          </td>
                          <td className="amount-cell-modern">${item.price?.toFixed(2)}</td>
                          <td className="amount-cell-modern">${(item.quantity * item.price).toFixed(2)}</td>
                          <td>
                            <button 
                              className="action-btn-modern restock-action" 
                              onClick={() => {
                                setUpdateForm({ 
                                  productId: item.id, 
                                  quantity: item.quantity 
                                });
                                setActiveTab('search');
                              }}
                              title="Restock Item"
                            >
                              📦
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'search' && (
        <div className="search-update-section">
          <div className="search-section">
            {/* Updated header structure for search section */}
            <div className="orders-header-section">
              <div className="orders-title-section">
                <h3>Search & Update Inventory</h3>
              </div>
              
              <div className="search-container-centered">
                <input
                  type="text"
                  placeholder="Search by product name, SKU, category, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input-centered"
                />
              </div>
              
              <div className="pagination-info-section">
                <span className="page-info">
                  Results: {filteredInventory.length}
                </span>
              </div>
            </div>

            <div className="filtered-results">
              {filteredInventory.length === 0 ? (
                <div className="no-results">
                  <p>No items match your search criteria.</p>
                </div>
              ) : (
                <div className="orders-table-modern">
                  <table>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Product Name</th>
                        <th>SKU</th>
                        <th>Category</th>
                        <th>Current Stock</th>
                        <th>Status</th>
                        <th>Unit Price</th>
                        <th>Total Value</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInventory.map(item => {
                        const stockStatus = getStockStatus(item.quantity);
                        return (
                          <tr key={item.id}>
                            <td className="order-id-cell">{item.id}</td>
                            <td className="product-name-cell">{item.name}</td>
                            <td className="customer-id-cell">{item.sku}</td>
                            <td className="category-cell">{item.category || 'N/A'}</td>
                            <td className="qty-cell-modern">
                              <span className={`quantity-display ${item.quantity < 10 ? 'low-stock' : ''}`}>
                                {item.quantity}
                              </span>
                            </td>
                            <td>
                              <span 
                                className="status-badge-modern"
                                style={{ 
                                  color: stockStatus.color, 
                                  backgroundColor: "white", 
                                  borderColor: stockStatus.color, 
                                  border: "2px solid"
                                }}
                              >
                                {stockStatus.text}
                              </span>
                            </td>
                            <td className="amount-cell-modern">${item.price?.toFixed(2)}</td>
                            <td className="amount-cell-modern">${(item.quantity * item.price).toFixed(2)}</td>
                            <td>
                              <button 
                                className="action-btn-modern" 
                                onClick={() => setUpdateForm({ 
                                  productId: item.id, 
                                  quantity: item.quantity 
                                })}
                                title="Update Stock"
                              >
                                ✏️
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {isAdmin && (
            <div className="update-section">
              <h3>Update Inventory</h3>
              <form onSubmit={updateInventoryQuantity} className="update-form">
                <div className="form-group">
                  <label htmlFor="updateProductId">Product:</label>
                  <select
                    id="updateProductId"
                    value={updateForm.productId}
                    onChange={(e) => setUpdateForm({...updateForm, productId: e.target.value})}
                    required
                    className="form-control"
                  >
                    <option value="">Select a product...</option>
                    {inventory.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.name} (Current: {item.quantity})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="updateQuantity">New Quantity:</label>
                  <input
                    type="number"
                    id="updateQuantity"
                    min="0"
                    value={updateForm.quantity}
                    onChange={(e) => setUpdateForm({...updateForm, quantity: parseInt(e.target.value)})}
                    required
                    className="form-control"
                  />
                </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    onClick={() => setUpdateForm({ productId: '', quantity: 0 })}
                    className="cancel-btn"
                  >
                    Clear
                  </button>
                  <button 
                    type="submit" 
                    disabled={updating || !isAdmin}
                    className="submit-btn"
                  >
                    {updating ? 'Updating...' : 'Update Inventory'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {!isAdmin && (
            <div className="admin-notice">
              <p>Admin privileges required to update inventory quantities.</p>
            </div>
          )}
        </div>
      )}

      {/* Item Details Modal */}
      {selectedItem && (
        <div className="modal-overlay" onClick={() => setSelectedItem(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Product Details - {selectedItem.name}</h3>
              <button 
                onClick={() => setSelectedItem(null)}
                className="modal-close"
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="details-section">
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Product ID:</label>
                    <span>{selectedItem.id}</span>
                  </div>
                  <div className="detail-item">
                    <label>Product Name:</label>
                    <span>{selectedItem.name}</span>
                  </div>
                  <div className="detail-item">
                    <label>SKU:</label>
                    <span>{selectedItem.sku}</span>
                  </div>
                  <div className="detail-item">
                    <label>Description:</label>
                    <span>{selectedItem.description || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Current Stock:</label>
                    <span className="quantity">{selectedItem.quantity}</span>
                  </div>
                  <div className="detail-item">
                    <label>Stock Status:</label>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStockStatus(selectedItem.quantity).color }}
                    >
                      {getStockStatus(selectedItem.quantity).text}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Unit Price:</label>
                    <span>${selectedItem.price?.toFixed(2)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Category:</label>
                    <span>{selectedItem.category || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Brand:</label>
                    <span>{selectedItem.brand || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Weight:</label>
                    <span>{selectedItem.weight ? `${selectedItem.weight} ${selectedItem.weightUnit || ''}` : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Active:</label>
                    <span>{selectedItem.active ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Total Value:</label>
                    <span>${(selectedItem.quantity * selectedItem.price).toFixed(2)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Last Updated:</label>
                    <span>{formatDate(selectedItem.updatedAt)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Updated By:</label>
                    <span>{selectedItem.updatedBy || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;